import React, { useEffect, useState } from "react";
import { apiFetch } from "./api";

const formatMoney = (rupees) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0
  }).format(rupees);

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  }) : "—";

const panel = {
  background: "#fff",
  border: "1px solid #e5eaf1",
  borderRadius: 14,
  padding: 22,
  marginBottom: 20
};

const field = {
  width: "100%",
  boxSizing: "border-box",
  padding: "11px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  fontSize: 14
};

export default function SubscriptionPage() {
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [method, setMethod] = useState("upi");
  const [reference, setReference] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  async function loadBilling() {
    const [plansResponse, subscriptionResponse, paymentsResponse] =
      await Promise.all([
        apiFetch("/billing/plans"),
        apiFetch("/billing/subscription"),
        apiFetch("/billing/payment-requests")
      ]);

    if (!plansResponse.ok || !subscriptionResponse.ok ||
        !paymentsResponse.ok) {
      throw new Error("Unable to load billing information");
    }

  const [planData, subscriptionData, paymentData] = await Promise.all([
      plansResponse.json(),
      subscriptionResponse.json(),
      paymentsResponse.json()
    ]);

    const available = planData.plans || [];
    setPlans(available);
    setSubscription(subscriptionData.subscription || null);
    setPayments(paymentData.paymentRequests || []);
    setSelectedPlan(previous =>
      available.some(plan => plan.code === previous)
        ? previous
        : (available[0]?.code || "")
    );
  }

  useEffect(() => {
    let active = true;

    async function initialize() {
      try {
        await loadBilling();
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    }

    initialize();
    return () => { active = false; };
  }, []);

  const chosenPlan = plans.find(plan => plan.code === selectedPlan);

  async function submitPayment(event) {
    event.preventDefault();
    if (submitting) return;

    setError("");
    setSuccess("");

    const parsedAmount = Number(amount);
    const paise = Math.round(parsedAmount * 100);

    if (!Number.isFinite(parsedAmount) ||
        parsedAmount <= 0 ||
        !Number.isSafeInteger(paise) ||
        Math.abs(parsedAmount * 100 - paise) > 0.000001) {
      setError("Enter a valid payment amount with up to two decimals.");
      return;
    }

    if (!selectedPlan || !reference.trim()) {
      setError("Select a plan and enter your transaction reference.");
      return;
    }

    setSubmitting(true);

    try {
      const response = await apiFetch("/billing/payments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan: selectedPlan,
          paymentMethod: method,
          paymentReference: reference.trim(),
          declaredAmountPaise: paise,
          customerNote: note.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Payment submission failed");
      }

      setSuccess(
        "Payment details submitted successfully. Verification is pending. " +
        "Your subscription will not activate until approval."
      );
      setReference("");
      setAmount("");
      setNote("");
      await loadBilling();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div style={panel}>Loading subscription information...</div>;
  }

  return (
    <main style={{
      maxWidth: 1050,
      margin: "24px auto",
      padding: "0 18px",
      color: "#17243b"
    }}>
      <h2 style={{ marginBottom: 6 }}>Subscription & Billing</h2>
      <p style={{ color: "#64748b", marginTop: 0 }}>
        Manage your plan and submit payment details for verification.
      </p>

      {error && (
        <div role="alert" style={{
          ...panel, background: "#fff1f2", color: "#9f1239"
        }}>{error}</div>
      )}

      {success && (
        <div role="status" style={{
          ...panel, background: "#effaf4", color: "#166534"
        }}>{success}</div>
      )}

      <section style={panel}>
        <h3>Current subscription</h3>
        <p><strong>Status:</strong> {subscription?.status || "Not available"}</p>
        <p><strong>Plan:</strong> {subscription?.plan || "—"}</p>
        {subscription?.status === "trial" && (
          <p><strong>Trial ends:</strong> {formatDate(subscription.trialEndsAt)}</p>
        )}
        {subscription?.currentPeriodEndsAt && (
          <p><strong>Paid period ends:</strong> {
            formatDate(subscription.currentPeriodEndsAt)
          }</p>
        )}
        {subscription?.scheduledPaidEndsAt && (
          <p><strong>Scheduled paid period ends:</strong> {
            formatDate(subscription.scheduledPaidEndsAt)
          }</p>
        )}
      </section>

      <section style={panel}>
        <h3>Choose your plan</h3>
        {plans.length === 0 ? (
          <p>No subscription plans are currently available.</p>
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
            gap: 14
          }}>
            {plans.map(plan => (
              <button
                type="button"
                key={plan.code}
                onClick={() => setSelectedPlan(plan.code)}
                aria-pressed={selectedPlan === plan.code}
                style={{
                  padding: 18,
                  borderRadius: 12,
                  textAlign: "left",
                  cursor: "pointer",
                  background: selectedPlan === plan.code
                    ? "#eff6ff" : "#fff",
                  border: selectedPlan === plan.code
                    ? "2px solid #2563eb" : "1px solid #cbd5e1"
                }}
              >
                <strong>{plan.name}</strong>
                <div style={{ fontSize: 25, marginTop: 12 }}>
                  {formatMoney(plan.price)}
                </div>
                <small>{plan.durationMonths} month(s)</small>
              </button>
            ))}
          </div>
        )}
      </section>

      <section style={panel}>
        <h3>Submit payment details</h3>
        <p style={{ color: "#64748b" }}>
          Complete your UPI or bank transfer using the payment instructions
          provided by MUKBI. Then submit the actual amount paid and transaction
          reference below. This form does not initiate a payment.
        </p>

        <form onSubmit={submitPayment} style={{
          display: "grid", gap: 14, maxWidth: 560
        }}>
          <label>
            <div style={{ marginBottom: 6 }}>Selected plan</div>
            <input
              style={{ ...field, background: "#f8fafc" }}
              value={chosenPlan
                ? `${chosenPlan.name} — ${formatMoney(chosenPlan.price)}`
                : "No plan available"}
              readOnly
            />
          </label>

          <label>
            <div style={{ marginBottom: 6 }}>Payment method</div>
            <select
              style={field}
              value={method}
              onChange={event => setMethod(event.target.value)}
            >
              <option value="upi">UPI</option>
              <option value="bank_transfer">Bank transfer</option>
            </select>
          </label>

          <label>
            <div style={{ marginBottom: 6 }}>Amount actually paid (₹)</div>
            <input
              style={field}
              type="number"
              min="0.01"
              step="0.01"
              required
              value={amount}
              onChange={event => setAmount(event.target.value)}
              placeholder="e.g. 799.00"
            />
          </label>

          <label>
            <div style={{ marginBottom: 6 }}>Transaction reference / UTR</div>
            <input
              style={field}
              maxLength={150}
              required
              value={reference}
              onChange={event => setReference(event.target.value)}
              placeholder="Enter your payment reference"
            />
          </label>

          <label>
            <div style={{ marginBottom: 6 }}>Additional note (optional)</div>
            <textarea
              style={field}
              maxLength={500}
              rows={3}
              value={note}
              onChange={event => setNote(event.target.value)}
            />
          </label>

          <button
            type="submit"
            disabled={submitting || !chosenPlan}
            style={{
              background: "#2563eb",
              color: "#fff",
              padding: 13,
              border: 0,
              borderRadius: 9,
              fontWeight: 600,
              cursor: submitting ? "wait" : "pointer",
              opacity: submitting || !chosenPlan ? 0.6 : 1
            }}
          >
            {submitting ? "Submitting..." : "Submit for verification"}
          </button>
        </form>
      </section>

      <section style={panel}>
        <h3>Payment request history</h3>
        {payments.length === 0 ? (
          <p>No payment requests submitted yet.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Date", "Plan", "Quoted amount", "Reference", "Status"]
                    .map(label => (
                      <th key={label} style={{
                        textAlign: "left",
                        padding: 10,
                        borderBottom: "1px solid #e2e8f0"
                      }}>{label}</th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {payments.map(payment => (
                  <tr key={payment._id}>
                    <td style={{ padding: 10 }}>
                      {formatDate(payment.submittedAt)}
                    </td>
                    <td style={{ padding: 10 }}>{payment.plan}</td>
                    <td style={{ padding: 10 }}>
                      {formatMoney(payment.amount)}
                    </td>
                    <td style={{ padding: 10 }}>
                      {payment.paymentReference}
                    </td>
                    <td style={{ padding: 10 }}>
                      {payment.status === "pending"
                        ? "Pending verification / approval"
                        : payment.status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
